import React, { createContext, useContext, useState } from 'react';
import { useNavigation } from '../hooks/useNavigation';

/**
 * Navigation Context
 * Provides sidebar control methods to components outside the sidebar
 */
export const NavigationContext = createContext({
  toggleSidebar: () => {},
  openSidebar: () => {},
  closeSidebar: () => {},
  isSidebarOpen: false,
  isNotifDrawerOpen: false,
  openNotifDrawer: () => {},
  closeNotifDrawer: () => {},
});

/**
 * Navigation Provider Component
 * Wraps the app to provide navigation context
 */
export const NavigationProvider = ({ children }) => {
  const navigation = useNavigation(false);
  const [isNotifDrawerOpen, setIsNotifDrawerOpen] = useState(false);

  const value = {
    toggleSidebar: navigation.toggle,
    openSidebar: navigation.open,
    closeSidebar: navigation.close,
    isSidebarOpen: navigation.isOpen,
    isNotifDrawerOpen,
    openNotifDrawer: () => setIsNotifDrawerOpen(true),
    closeNotifDrawer: () => setIsNotifDrawerOpen(false),
    // Internal methods for Sidebar component
    _internal: {
      activeItemId: navigation.activeItemId,
      setActive: navigation.setActive,
      expandedItems: navigation.expandedItems,
      toggleExpanded: navigation.toggleExpanded,
      expand: navigation.expand,
      collapse: navigation.collapse,
      isExpanded: navigation.isExpanded,
    },
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Hook to access navigation context
 * @returns {Object} Navigation control methods
 */
export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationContext must be used within NavigationProvider');
  }
  return context;
};
